import * as Sentry from '@sentry/react';

export function initializeSentry() {
  // Only initialize in production
  if (import.meta.env.MODE !== 'production') {
    console.log('Sentry: Skipping initialization in development mode');
    return;
  }

  const dsn = import.meta.env.VITE_SENTRY_DSN;
  
  if (!dsn) {
    console.warn('Sentry: DSN not configured');
    return;
  }

  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,
    
    // Performance Monitoring
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    
    // Performance trace sampling - 10% in production
    tracesSampleRate: 0.1,
    
    // Session Replay - capture 100% of sessions with errors
    replaysSessionSampleRate: 0, // Don't record normal sessions
    replaysOnErrorSampleRate: 1.0, // Record 100% of sessions with errors
    
    // Filter sensitive data before sending to Sentry
    beforeSend(event, hint) {
      // Remove sensitive data from event
      if (event.request?.data) {
        const data = event.request.data;
        
        // Remove password fields
        if (typeof data === 'object' && data !== null) {
          const sanitized = { ...data };
          const sensitiveFields = ['password', 'pin', 'token', 'secret', 'apiKey', 'creditCard'];
          
          sensitiveFields.forEach(field => {
            if (field in sanitized) {
              sanitized[field] = '[Filtered]';
            }
          });
          
          event.request.data = sanitized;
        }
      }
      
      return event;
    },
    
    // Configure which URLs to trace
    tracePropagationTargets: [
      'localhost',
      /^https:\/\/uglcvrexpjcgfvwwlarh\.supabase\.co/,
    ],
  });

  console.log('Sentry: Initialized successfully');
}

// Helper function to manually capture exceptions with context
export function captureException(error: Error, context?: Record<string, any>) {
  Sentry.captureException(error, {
    extra: context,
  });
}

// Helper function to track custom transactions
export function startTransaction(name: string, operation: string) {
  return Sentry.startSpan(
    {
      name,
      op: operation,
    },
    () => {}
  );
}

// Set user context
export function setUser(user: { id: string; email?: string; username?: string } | null) {
  Sentry.setUser(user);
}

// Add breadcrumb for tracking user actions
export function addBreadcrumb(message: string, data?: Record<string, any>) {
  Sentry.addBreadcrumb({
    message,
    data,
    level: 'info',
  });
}
